"use client";
import React, { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import ReactDOM from "react-dom";
import ReactSelect from "react-select";
import { CustomTooltip } from "./tooltip";

interface Option {
  value: string | number;
  label: string;
  icon?: React.ReactNode;
}

interface SelectProps {
  onChange: (value: string | number | (string | number)[]) => void;
  options: Option[];
  label?: string;
  value: string | number | (string | number)[];
  id?: string;
  className?: string;
  containerClassName?: string;
  style?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "error"
    | "outlined";
  multi?: boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
}

export function Select({
  options,
  label,
  value,
  className = "",
  containerClassName = "",
  style = "outlined",
  multi = false,
  onChange,
  disabled = false,
  error,
  placeholder = "Seleccionar...",
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Estado para controlar si el texto se está desbordando en el input principal
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const selectedOptions = (options || []).filter(
    (opt) => Array.isArray(value) && value.includes(opt.value),
  );

  const filteredOptions = (options || []).filter((opt) =>
    opt?.label?.toLowerCase().includes(search.toLowerCase()),
  );

  // Función para verificar si el texto actual excede el ancho de su contenedor
  const checkTruncation = () => {
    if (textRef.current) {
      const { scrollWidth, clientWidth } = textRef.current;
      setIsTextTruncated(scrollWidth > clientWidth);
    }
  };

  // Validar si el texto se corta al abrir/cerrar o cambiar de valor
  useEffect(() => {
    checkTruncation();
  }, [value, placeholder]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setSearch("");
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const updateMenuPosition = () => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const horizontalMargin = 12;
    const menuWidth = Math.min(
      Math.max(rect.width, 300),
      viewportWidth - horizontalMargin * 2,
    );
    const left = Math.min(
      Math.max(rect.left, horizontalMargin),
      viewportWidth - menuWidth - horizontalMargin,
    );

    setMenuPosition({
      top: rect.bottom + 4,
      left,
      width: menuWidth,
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  // Manejador para cuando la ventana cambie de tamaño (por si cambia el ancho del select)
  useEffect(() => {
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, []);

  const styleClasses = {
    default: "border-gray-300 focus:border-blue-500",
    primary: "border-primary text-primary font-medium",
    secondary: "border-gray-500",
    success: "border-green-500",
    warning: "border-yellow-500",
    error: "border-red-500",
    outlined: "border-primary hover:border-primary/90",
  };

  const styleArrowClasses = {
    default: "text-gray-400",
    primary: "text-primary",
    secondary: "text-gray-400",
    success: "text-green-500",
    warning: "text-yellow-500",
    error: "text-red-500",
    outlined: "text-primary",
  };

  const selectedOption = (options || []).find((opt) => opt.value === value);

  // Renderizado para selección múltiple (ReactSelect maneja sus propios tooltips internamente si lo requieres)
  if (multi) {
    return (
      <div className={cn("w-full flex flex-col gap-1.5", containerClassName)}>
        {label && (
          <p className={cn("mb-0 text-sm ", disabled && "opacity-50")}>
            {label}
          </p>
        )}
        <ReactSelect
          isMulti
          isDisabled={disabled}
          value={selectedOptions}
          onChange={(selected) => {
            const values = Array.isArray(selected)
              ? (selected as Option[]).map((opt) => opt.value)
              : [];
            onChange && onChange(values);
          }}
          options={options || []}
          menuPortalTarget={
            typeof window !== "undefined" ? document.body : undefined
          }
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            dropdownIndicator: (base) => ({
              ...base,
              color:
                style === "primary" || style === "outlined"
                  ? "var(--primary)"
                  : "#9ca3af",
            }),
            control: (base, state) => ({
              ...base,
              borderRadius: "0.375rem",
              borderColor: error
                ? "#ef4444"
                : style === "primary" || style === "outlined"
                  ? "var(--primary)"
                  : state.isFocused
                    ? "#3b82f6"
                    : "#e5e7eb",
              boxShadow: state.isFocused
                ? `0 0 0 1px ${error ? "#ef4444" : "#3b82f6"}`
                : "none",
              "&:hover": { borderColor: error ? "#ef4444" : "#3b82f6" },
            }),
          }}
          placeholder={placeholder}
        />
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </div>
    );
  }

  const menuContent = (
    <div className="bg-white border border-gray-200 rounded-md shadow-xl overflow-hidden animate-in fade-in zoom-in duration-100 fill-mode-both">
      {(options?.length || 0) > 5 && (
        <div className="p-2 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2 px-3 border border-gray-200 rounded-md bg-white focus-within:border-primary transition-colors h-10 shadow-sm">
            <svg
              className="w-4 h-4 text-gray-400 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="flex-1 bg-transparent border-none outline-none ring-0 focus:ring-0 text-sm text-slate-700 p-0"
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
      <ul className="max-h-64 overflow-y-auto focus:outline-none py-1 list-none p-0 m-0 w-full scrollbar-thin scrollbar-thumb-gray-200">
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <li
              key={option.value}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(option.value);
                setIsOpen(false);
                setSearch("");
              }}
              className={cn(
                "px-4 py-2 text-sm cursor-pointer hover:bg-slate-50 transition-colors w-full text-left list-none block truncate",
                option.value === value
                  ? "bg-blue-50 text-blue-700 font-semibold"
                  : "text-gray-700",
              )}
            >
              <div className="flex items-center gap-2 truncate">
                {option.icon && <span className="shrink-0">{option.icon}</span>}
                <span className="truncate">{option.label}</span>
              </div>
            </li>
          ))
        ) : (
          <li className="px-4 py-3 text-xs text-gray-400 text-center font-medium list-none block w-full">
            Sin resultados
          </li>
        )}
      </ul>
    </div>
  );

  const currentLabel = selectedOption?.label || placeholder;

  return (
    <div
      className={cn(
        "relative w-full flex flex-col gap-1.5",
        containerClassName,
      )}
      ref={containerRef}
    >
      {label && (
        <p className={cn("mb-0 text-sm", disabled && "opacity-90")}>{label}</p>
      )}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          "px-4 flex items-center justify-between w-full min-h-10 border rounded-md bg-white cursor-pointer transition-all text-sm gap-2",
          error ? styleClasses.error : styleClasses[style],
          disabled ? "opacity-70 cursor-not-allowed" : "hover:border-gray-400",
          isOpen && "ring-1 ring-primary border-primary",
          className,
        )}
      >
        {/* INTEGRACIÓN DE TU CUSTOMTOOLTIP: 
          Pasamos el contenido solo si el texto real es más largo que el espacio visible (isTextTruncated)
        */}
        <CustomTooltip
          content={isTextTruncated ? currentLabel : null}
          side="top"
          sideOffset={6}
        >
          <span
            ref={textRef}
            className={cn(
              " flex-1 text-left flex items-center gap-2 truncate",
              selectedOption ? "text-slate-700" : "text-gray-400",
            )}
          >
            {selectedOption?.icon && (
              <span className="shrink-0">{selectedOption.icon}</span>
            )}
            <span className="truncate">{currentLabel}</span>
          </span>
        </CustomTooltip>

        <svg
          className={cn(
            "w-4 h-4 transition-transform shrink-0",
            isOpen && "rotate-180",
            styleArrowClasses[style],
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {isOpen &&
        typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            onMouseDown={(e) => e.stopPropagation()}
            className="fixed z-[9999] shadow-2xl"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
            }}
          >
            {menuContent}
          </div>,
          document.body,
        )}

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}
